import { useEffect, useRef, useState } from 'react'
import { animate, useInView, useReducedMotion } from 'framer-motion'

function formatNumber(value, decimals) {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

/** Counts up to `to` the first time it scrolls into view, and again whenever `to` changes. */
function CountUp({ to, decimals = 0, suffix = '' }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' })
  const reduceMotion = useReducedMotion()
  const [animated, setAnimated] = useState(0)
  const shown = reduceMotion ? to : animated
  const fromRef = useRef(0)

  useEffect(() => {
    if (!inView) return undefined
    if (reduceMotion) return undefined
    const controls = animate(fromRef.current, to, {
      duration: 1.5,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (latest) => {
        fromRef.current = latest
        setAnimated(latest)
      },
    })
    return () => controls.stop()
  }, [inView, to, reduceMotion])

  return (
    <span ref={ref}>
      {formatNumber(shown, decimals)}
      {suffix}
    </span>
  )
}

/** One bordered strip of headline numbers, in the same language as the dashboard overview. */
export default function StatsStrip({ stats }) {
  return (
    <dl className="grid grid-cols-2 gap-px border border-m1-line bg-m1-line lg:grid-cols-4">
      {stats.map((stat) => (
        <div key={stat.label} className="bg-m1-ground p-5 sm:p-6">
          <dt className="font-m1-mono text-[11px] uppercase tracking-[0.14em] text-m1-muted">
            {stat.label}
          </dt>
          <dd className="mt-4 text-[clamp(32px,4vw,44px)] font-light leading-none tabular-nums text-m1-ink">
            <CountUp to={stat.value} decimals={stat.decimals} suffix={stat.suffix} />
          </dd>
        </div>
      ))}
    </dl>
  )
}
